import { utmUtils } from "@/utils/utm";
import { useMutation } from "@apollo/client";
import { useSearchParams } from "next/navigation";
import { useCallback, useState } from "react";
import { useFormContext } from "../context/FormProvider";
import { CREATE_OR_UPDATE_RAW_UNIT } from "./useGraphQLQueries";
interface MutationResult {
  id?: string;
  groupId?: string;
  success?: boolean;
  message?: string;
  rawUnitId?: string;
  sellerId?: string;
}
interface UseFormMutationReturn {
  mutate: (createMode?: boolean, dynamicFormData?: unknown) => Promise<MutationResult | null>;
  loading: boolean;
  error: Error | null;
  data: MutationResult | null;
  retry: () => Promise<MutationResult | null>;
  isNetworkError: boolean;
  hasPendingMutation: boolean;
  retryCount: number;
}
const isNetworkError = (error: unknown): boolean => {
  if (!error) return false;
  const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
  return errorMessage.includes("network") || errorMessage.includes("fetch") || errorMessage.includes("connection") || errorMessage.includes("timeout") || errorMessage.includes("offline") || errorMessage.includes("unreachable") || errorMessage.includes("failed to fetch") || errorMessage.includes("networkerror");
};
const getBackoffDelay = (retryCount: number): number => {
  const delays = [1000, 3000, 5000];
  return delays[Math.min(retryCount, delays.length - 1)];
};
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));
export function useFormMutation(stage: string, formData: unknown, sellerGroupId: string): UseFormMutationReturn {
  const {
    state
  } = useFormContext();
  const relationship = state.profile?.relationship || "";
  const searchParams = useSearchParams();
  const rawUnitId = searchParams?.get("rawUnitId");
  const groupId = searchParams?.get("groupId");
  const groupType = ["owner", "agent"].includes(relationship) ? "individual" : relationship;
  const [retryCount, setRetryCount] = useState(0);
  const [pendingMutation, setPendingMutation] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const [lastError, setLastError] = useState<Error | null>(null);
  const [createOrUpdateRawUnit, {
    loading,
    error,
    data: d
  }] = useMutation<{
    rawUnit: {
      createOrUpdateRawUnit: MutationResult;
    };
  }>(CREATE_OR_UPDATE_RAW_UNIT);
  const data = d?.rawUnit?.createOrUpdateRawUnit || null;
  const executeMutationWithRetry = useCallback(async (createMode = false, currentRetryCount = 0, dynamicFormData?: unknown): Promise<MutationResult | null> => {
    try {
      setNetworkError(false);
      setLastError(null);
      // Use dynamic form data if provided, otherwise fall back to the original formData
      const dataToSubmit = dynamicFormData !== undefined ? dynamicFormData : formData;
      const result = await createOrUpdateRawUnit({
        variables: {
          params: {
            createOrUpdateRawUnit: {
              id: rawUnitId,
              sellerId: sellerGroupId,
              data: dataToSubmit,
              updateGroup: ["builder", "landOwner", "investor"].includes(groupType || ""),
              stage,
              utm: utmUtils.getCurrent()
            }
          },
          skip: stage !== "profile" && !rawUnitId && !groupId
        },
        errorPolicy: "all"
      });
      const mutationResult = result.data?.rawUnit?.createOrUpdateRawUnit;
      if (mutationResult) {
        setPendingMutation(false);
        setRetryCount(0);
        return mutationResult;
      }
      return null;
    } catch (error_0) {
      const isNetworkErr = isNetworkError(error_0);
      setNetworkError(isNetworkErr);
      setLastError(error_0 instanceof Error ? error_0 : new Error(String(error_0)));
      if (isNetworkErr && currentRetryCount < 3) {
        const delay = getBackoffDelay(currentRetryCount);
        setRetryCount(currentRetryCount + 1);
        setPendingMutation(true);
        await sleep(delay);
        return executeMutationWithRetry(createMode, currentRetryCount + 1, dynamicFormData);
      } else {
        setPendingMutation(true);
        throw error_0;
      }
    }
  }, [rawUnitId, relationship, stage, formData, createOrUpdateRawUnit, sellerGroupId]);
  const executeMutation = useCallback(async (createMode_0 = false, dynamicFormData_0?: unknown): Promise<MutationResult | null> => {
    setRetryCount(0);
    return executeMutationWithRetry(createMode_0, 0, dynamicFormData_0);
  }, [executeMutationWithRetry]);
  const retry = useCallback(async (): Promise<MutationResult | null> => {
    if (!pendingMutation) {
      return executeMutation();
    }
    setRetryCount(0);
    setPendingMutation(false);
    return executeMutationWithRetry(false, 0, formData);
  }, [pendingMutation, executeMutation, executeMutationWithRetry, formData]);
  return {
    mutate: executeMutation,
    loading: loading || pendingMutation,
    error: lastError || (error ? new Error(error.message) : null),
    data,
    retry,
    isNetworkError: networkError,
    hasPendingMutation: pendingMutation,
    retryCount
  };
}